import { useCallback, useEffect, useMemo, useState } from 'react';
import referenceValuesService from '../services/referenceValuesService';
import PendingPanel from '../components/referenceValues/PendingPanel';
import ValuesByYear from '../components/referenceValues/ValuesByYear';
import EditValueModal from '../components/referenceValues/EditValueModal';
import RejectModal from '../components/referenceValues/RejectModal';
import { formatWhen, pendingRows, runSummary } from '../components/referenceValues/helpers';

export const REVIEWER_PERMISSION = 'reference_values_admin';

const errorText = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return err?.message || 'The request failed.';
};

const reviewItems = (rows) => rows.map((row) => ({
  year: row.year, key: row.value_key, expected_value: row.proposed.value,
}));

/**
 * Reference Values: the annual IRS/SSA limits eMoney Build and Client Helper
 * are given. A daily job reads the IRS and SSA publications and proposes new
 * or changed figures; reviewers approve them here before any prompt uses them.
 */
const ReferenceValuesPage = () => {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [modalError, setModalError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, latestRuns] = await Promise.all([referenceValuesService.list(), referenceValuesService.runs(5)]);
      setData(list);
      setRuns(latestRuns);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const catalogOrder = useMemo(() => {
    const order = {};
    (data?.catalog?.values || []).forEach((spec, i) => { order[spec.key] = i; });
    return order;
  }, [data]);
  const pending = useMemo(() => pendingRows(data?.values || [], catalogOrder), [data, catalogOrder]);

  const report = (results, verb) => {
    const failed = results.filter((r) => !r.ok);
    const done = results.length - failed.length;
    setMessage({
      kind: failed.length ? 'warning' : 'success',
      text: `${done} ${verb}.${failed.length ? ` ${failed.length} not: ${failed[0].error}` : ''}`,
    });
  };

  const approve = async (rows) => {
    setBusy(true);
    setMessage(null);
    try {
      report(await referenceValuesService.review(reviewItems(rows), 'approve'), 'approved');
      await load();
    } catch (err) {
      setMessage({ kind: 'danger', text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const reject = async (note) => {
    setBusy(true);
    setModalError(null);
    try {
      report(await referenceValuesService.review(reviewItems(rejecting), 'reject', note), 'rejected');
      setRejecting(null);
      await load();
    } catch (err) {
      setModalError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const save = async (body) => {
    setBusy(true);
    setModalError(null);
    try {
      await referenceValuesService.setValue(editing.year, editing.spec.key, body);
      setMessage({ kind: 'success', text: `${editing.year} ${editing.spec.label} published.` });
      setEditing(null);
      await load();
    } catch (err) {
      setModalError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await referenceValuesService.refresh();
      setMessage({ kind: 'info', text: 'Refresh started. It takes a minute or two; reload to see what it found.' });
    } catch (err) {
      setMessage({ kind: 'danger', text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const lastRun = runs[0];

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">
          <i className="bi bi-journal-check me-2"></i>
          Reference Values
        </h1>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={load} disabled={busy}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            Reload
          </button>
          <button type="button" className="btn btn-outline-primary" onClick={runNow} disabled={busy}>
            <i className="bi bi-cloud-download me-1"></i>
            Check sources now
          </button>
        </div>
      </div>

      <p className="text-muted">
        Annual IRS and Social Security limits given to eMoney Build and Client Helper. They are read daily from
        the IRS and SSA publications; a new or changed figure is used only once it has been approved here.
      </p>

      {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}
      {message && <div className={`alert alert-${message.kind}`} role="status">{message.text}</div>}

      <div className="small text-muted mb-3" data-testid="last-run">
        Last check: {lastRun ? `${formatWhen(lastRun.started_at)} — ${runSummary(lastRun)}` : runSummary(null)}
      </div>
      {lastRun?.failures?.length > 0 && (
        <div className="alert alert-warning" role="alert">
          <strong>Problems in the last check</strong>
          <ul className="mb-0">
            {lastRun.failures.map((failure) => <li key={failure}>{failure}</li>)}
          </ul>
        </div>
      )}

      {data && (
        <>
          <PendingPanel
            rows={pending}
            busy={busy}
            onApprove={approve}
            onReject={(rows) => { setModalError(null); setRejecting(rows); }}
          />
          <ValuesByYear
            catalog={data.catalog}
            rows={data.values}
            currentYear={data.current_year}
            onEdit={(year, spec, row) => { setModalError(null); setEditing({ year, spec, row }); }}
          />
        </>
      )}
      {!data && !loadError && <div className="text-muted">Loading…</div>}

      {rejecting && (
        <RejectModal
          count={rejecting.length}
          busy={busy}
          error={modalError}
          onConfirm={reject}
          onClose={() => setRejecting(null)}
        />
      )}
      {editing && (
        <EditValueModal
          year={editing.year}
          spec={editing.spec}
          row={editing.row}
          busy={busy}
          error={modalError}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default ReferenceValuesPage;
